import React, { useState } from 'react';
import { useLang } from '../../context/LangContext';
import { ScanTarget } from '@shared/types';
import { SingleTargetInput } from './SingleTargetInput';
import { IPRangeInput } from './IPRangeInput';
import { FileUploadInput } from './FileUploadInput';
import { ProtocolScanInput } from './ProtocolScanInput';

interface ScanInputContainerProps {
  onStartScan: (targets: ScanTarget[]) => void;
  isScanning?: boolean;
  isAdvanced?: boolean;
  isIpRange?: boolean;
  isEnterprise?: boolean;
  token?: string | null;
  enabledFeatures?: string[];
  onProtocolResult?: (data: any) => void;
}

type InputMode = 'single' | 'range' | 'file' | 'protocol';

export const ScanInputContainer: React.FC<ScanInputContainerProps> = ({
  onStartScan,
  isScanning = false,
  isAdvanced = false,
  isIpRange = false,
  isEnterprise = false,
  token = null,
  enabledFeatures = [],
  onProtocolResult,
}) => {
  const { lang } = useLang();
  const [activeMode, setActiveMode] = useState<InputMode>('single');
  const [targets, setTargets] = useState<ScanTarget[]>([]);
  const [error, setError] = useState<string>('');

  const handleTargetAdd = (target: ScanTarget) => {
    // In single mode, directly start scan
    if (activeMode === 'single') {
      onStartScan([target]);
      return;
    }

    // Check for duplicates
    const isDuplicate = targets.some(
      t => t.value === target.value && t.port === target.port
    );

    if (isDuplicate) {
      setError(lang === 'zh' ? `目標 ${target.value}:${target.port} 已存在` : `Target ${target.value}:${target.port} already exists`);
      return;
    }

    setTargets(prev => [...prev, target]);
    setError('');
  };

  const handleTargetsAdd = (newTargets: ScanTarget[]) => {
    // Filter out duplicates
    const uniqueTargets = newTargets.filter(newTarget => 
      !targets.some(existing => 
        existing.value === newTarget.value && existing.port === newTarget.port
      )
    );

    if (uniqueTargets.length !== newTargets.length) {
      const duplicateCount = newTargets.length - uniqueTargets.length;
      setError(`${duplicateCount} duplicate target(s) were skipped`);
    } else {
      setError('');
    }

    setTargets(prev => [...prev, ...uniqueTargets]);
  };

  const handleRemoveTarget = (index: number) => {
    setTargets(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setTargets([]);
    setError('');
  };

  const handleStartScan = () => {
    if (targets.length === 0) {
      setError(lang === 'zh' ? '請至少新增一個目標' : 'Please add at least one target');
      return;
    }

    onStartScan(targets);
  };

  const renderInputComponent = () => {
    switch (activeMode) {
      case 'single':
        return (
          <SingleTargetInput
            onTargetAdd={handleTargetAdd}
            disabled={isScanning}
          />
        );
      case 'range':
        return (
          <IPRangeInput
            onTargetsAdd={handleTargetsAdd}
            disabled={isScanning}
          />
        );
      case 'file':
        return (
          <FileUploadInput
            onTargetsAdd={handleTargetsAdd}
            disabled={isScanning}
          />
        );
      case 'protocol':
        return (
          <ProtocolScanInput token={token} disabled={isScanning} lang={lang} enabledFeatures={enabledFeatures} onResult={onProtocolResult} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="scan-input-container">
      <div className="input-mode-selector">
        <button
          className={`mode-btn ${activeMode === 'single' ? 'active' : ''}`}
          onClick={() => setActiveMode('single')}
          disabled={isScanning}
        >
          {lang === 'zh' ? '單次掃描' : 'Single Scan'}
        </button>
        <button
          className={`mode-btn ${activeMode === 'range' ? 'active' : ''}`}
          onClick={() => !isIpRange ? undefined : setActiveMode('range')}
          disabled={isScanning || !isIpRange}
          title={!isIpRange ? 'Advanced+ required' : undefined}
          style={!isIpRange ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          {lang === 'zh' ? 'IP 範圍' : 'IP Range'} {!isIpRange && '🔒'}
        </button>
        <button
          className={`mode-btn ${activeMode === 'file' ? 'active' : ''}`}
          onClick={() => !isAdvanced ? undefined : setActiveMode('file')}
          disabled={isScanning || !isAdvanced}
          title={!isAdvanced ? '需要進階會員才能使用' : undefined}
          style={!isAdvanced ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          {lang === 'zh' ? '批次掃描' : 'Batch Scan'} {!isAdvanced && '🔒'}
        </button>
        {(() => {
          const hasProtocol = enabledFeatures.includes('ssh_scan') || enabledFeatures.includes('email_scan') || enabledFeatures.includes('dnssec_scan');
          return (
            <button
              className={`mode-btn ${activeMode === 'protocol' ? 'active' : ''}`}
              onClick={() => hasProtocol ? setActiveMode('protocol') : undefined}
              disabled={isScanning || !hasProtocol}
              title={!hasProtocol ? 'Upgrade required' : undefined}
              style={!hasProtocol ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              Protocol {!hasProtocol && '🔒'}
            </button>
          );
        })()}
      </div>

      <div className="input-component">
        {renderInputComponent()}
      </div>

      {error && <div className="error-message">{error}</div>}

      {targets.length > 0 && (
        <div className="targets-list">
          <div className="targets-header">
            <h4>{lang === 'zh' ? `掃描目標 (${targets.length})` : `Scan Targets (${targets.length})`}</h4>
            <button 
              onClick={handleClearAll}
              disabled={isScanning}
              className="clear-all-btn"
            >
              {lang === 'zh' ? '清除全部' : 'Clear All'}
            </button>
          </div>

          <div className="targets-table">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Target</th>
                  <th>Port</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`target-type ${target.type}`}>
                        {target.type.toUpperCase()}
                      </span>
                    </td>
                    <td>{target.value}</td>
                    <td>{target.port}</td>
                    <td>
                      <button
                        onClick={() => handleRemoveTarget(index)}
                        disabled={isScanning}
                        className="remove-btn"
                      >
                        {lang === 'zh' ? '移除' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="scan-actions">
            <button
              onClick={handleStartScan}
              disabled={isScanning || targets.length === 0}
              className="start-scan-btn"
            >
              {isScanning ? (lang === 'zh' ? '掃描中...' : 'Scanning...') : (lang === 'zh' ? `開始掃描 (${targets.length} 個目標)` : `Start Scan (${targets.length} targets)`)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};